import React, { useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";

const initialFuelData = [
  {
    FVehicleName: "14161647151",
    FStartTime: "2022-04-27T09:01:03",
    FEndTime: "2022-04-27T09:06:05",
    FStartLiter: 70,
    FEndLiter: 448,
    RowNo: 1,
  },
  {
    FVehicleName: "16273849123",
    FStartTime: "2022-04-27T10:01:03",
    FEndTime: "2022-04-27T10:15:05",
    FStartLiter: 150,
    FEndLiter: 350,
    RowNo: 2,
  },
  // Add more data as needed
];

// Helper to extract unique vehicles for the dropdown
const getVehicleOptions = (data) =>
  [...new Set(data.map((entry) => entry.FVehicleName))].map((name) => ({
    value: name,
    label: name,
  }));

const FuelTrajectoryGraph = () => {
  const [liveData, setLiveData] = useState(initialFuelData);
  const [filteredData, setFilteredData] = useState(initialFuelData);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const ws = useRef(null);

  useEffect(() => {
    ws.current = new WebSocket("ws://localhost:5000");

    ws.current.onopen = () => {
      console.log("Connected to WebSocket server");
    };

    ws.current.onmessage = (event) => {
      try {
        const newData = JSON.parse(event.data);
        // newData expected shape: { guid, timestamp, fuel, rate, status }

        // Format date string if needed:
        const formattedTime = new Date(newData.timestamp).toISOString();

        // Construct new entry to match chart data format
        const newEntry = {
          FVehicleName: newData.guid,
          FStartTime: formattedTime,
          FEndTime: formattedTime,
          FStartLiter: parseFloat(newData.fuel),
          FEndLiter: parseFloat(newData.fuel),
          RowNo: liveData.length + 1,
          status: newData.status,
          rate: newData.rate,
        };

        setLiveData((prev) => [...prev, newEntry]);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.current.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.current.onclose = () => {
      console.log("Disconnected from WebSocket server");
    };

    return () => {
      if (ws.current) ws.current.close();
    };
  }, []);

  const handleFilter = () => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    const filtered = liveData.filter((entry) => {
      const entryTime = new Date(entry.FStartTime);
      return (
        (!start || entryTime >= start) &&
        (!end || entryTime <= end) &&
        (!selectedVehicle || entry.FVehicleName === selectedVehicle.value)
      );
    });

    setFilteredData(filtered);
  };

  return (
    <div className="flex flex-col items-center w-full h-screen px-4">
      <div className="mb-4 flex items-center gap-4 mt-8 max-md:flex-wrap">
        <div className="flex items-center gap-2">
          <label>Vehicle: </label>
          <Select
            options={getVehicleOptions(liveData)}
            value={selectedVehicle}
            onChange={setSelectedVehicle}
            isClearable
            placeholder="Select a vehicle"
            className="w-60"
          />
        </div>
        <div>
          <label>Start Date: </label>
          <DatePicker
            selected={startDate}
            className="border rounded-lg p-1"
            showTimeSelect
            onChange={(date) => setStartDate(date)}
          />
        </div>
        <div>
          <label>End Date: </label>
          <DatePicker
            selected={endDate}
            className="border rounded-lg p-1"
            showTimeSelect
            onChange={(date) => setEndDate(date)}
          />
        </div>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
          onClick={handleFilter}
        >
          Query
        </button>
      </div>
      <div className="flex-1 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={filteredData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="FStartTime"
              tickFormatter={(tick) =>
                new Date(tick).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              }
              type="category"
            />
            <YAxis />
            <Tooltip
              labelFormatter={(label) => new Date(label).toLocaleString()}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="FStartLiter"
              stroke="#8884d8"
              name="Start Liters"
            />
            <Line
              type="monotone"
              dataKey="FEndLiter"
              stroke="#82ca9d"
              name="End Liters"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default FuelTrajectoryGraph;
